import { ApolloError } from '@apollo/client';
import jwt_decode, { JwtPayload } from 'jwt-decode';

import { getIdToken } from './api';
import { isFirstGraphQLErrorUnauthorized } from 'utilities/errors';

/**
 * Provides a common interface for web/native bug tracker.
 * On web, we currently use Honeybadger
 *  */
export interface BugTrackerInterface {
  notify(...args: any[]): void;
}

const noopTracker: BugTrackerInterface = {
  notify: () => {}
};

class BugTrackerWrapper implements BugTrackerInterface {
  inner: BugTrackerInterface = noopTracker;

  notify(...args: any[]) {
    const [error] = args;

    if (this.shouldSkipUnauthorizedError(error) || isLoadedFromFileSystem()) {
      return;
    }

    try {
      this.inner.notify(...args);
    } catch (notificationError) {
      // Prevent unhandled errors from breaking error reporting
      // Log to console as fallback when the bug tracker itself fails
      console.error('BugTracker notification failed:', notificationError);
    }
  }

  // check for unauthorized errors and verify if it happen because of an expired token
  private shouldSkipUnauthorizedError(error: any): boolean {
    const isUnauthorizedErrorType =
      error instanceof ApolloError && isFirstGraphQLErrorUnauthorized(error);

    if (!isUnauthorizedErrorType) return false;

    const idToken = getIdToken();
    if (!idToken) return true; // skip if it's unauthorized error and token doesn't exist anymore

    try {
      const maybeJwt = jwt_decode(idToken) as JwtPayload;
      const hourMs = 60 * 60 * 1000;
      // We need to look at iat + 1hour for token expiry because not all auth services issue tokens with the same exp duration(SSO ones)
      // Our auth logic looks at iat + 1hour instead.
      const tokenIssuedAtMs = maybeJwt?.iat ? maybeJwt.iat * 1000 : 0;
      const isTokenExpired = tokenIssuedAtMs + hourMs < Date.now();

      return isTokenExpired;
    } catch {
      // skip if it's unauthorized error and token can't be decoded
      return true;
    }
  }
}

export const BugTracker = new BugTrackerWrapper();

export function setBugTracker(tracker: BugTrackerInterface) {
  BugTracker.inner = tracker;
}

export const isLoadedFromFileSystem = () => window?.location?.protocol === 'file:';
